import { memo, useEffect, useRef, useState } from 'react'
import { ArrowLeft, Info, MoreVertical } from 'lucide-react'
import { formatTimestamp } from '@vc/services/format'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import type { ChatAttachmentKind, ChatMessage } from '../models/chat.model'
import { ChatAttachmentBubble } from './ChatAttachmentBubble'
import { ChatComposer } from './ChatComposer'

const PASTEL_COLORS = [
  '#E8B8C0', '#B8CFE8', '#B8E8B8', '#CFC0E8', '#E8D0B8',
  '#E8C0D8', '#B8E8D8', '#E8DFB8', '#C8C0E8', '#E8B8B8',
  '#D0E8B8', '#B8E8E8', '#E8D8B8', '#CFC0E8', '#E8C0D8',
  '#B8D8E8', '#E0C0E8', '#B8E8C8', '#E8D0B8', '#C8C0E8',
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return PASTEL_COLORS[Math.abs(hash) % PASTEL_COLORS.length]
}

interface PrivateChatViewProps {
  participant: {
    id: string
    name: string
    avatar?: string
  }
  messages: ChatMessage[]
  localUserId: string
  onSend: (text: string, recipientId: string) => void
  /**
   * Fires when the user picks a file via the composer's attach menu. The
   * parent should append an attachment-only ChatMessage to this private
   * conversation's history (same shape as the group-chat handler in
   * `ChatPanel.handleAttach`).
   */
  onAttach?: (file: File, kind: ChatAttachmentKind, recipientId: string) => void
  onBack: () => void
  onSaveChat: () => void
  onClearChat: () => void
  /** Host gates — passed straight through to the composer. */
  /** False when the host has closed private chat: the composer says so instead. */
  canChat?: boolean
  canSendFiles?: boolean
  canUseEmojis?: boolean
}

export const PrivateChatView = memo(function PrivateChatView({
  participant,
  messages,
  localUserId,
  onSend,
  onAttach,
  onBack,
  onSaveChat,
  onClearChat,
  canChat = true,
  canSendFiles = true,
  canUseEmojis = true,
}: PrivateChatViewProps) {
  const t = useTranslation()
  const [text, setText] = useState('')
  const [showMenu, setShowMenu] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  useEffect(() => {
    if (!showMenu) return
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  const handleSend = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSend(trimmed, participant.id)
    setText('')
  }

  const handleAttach = (file: File, kind: ChatAttachmentKind) => {
    onAttach?.(file, kind, participant.id)
  }

  return (
    <div className="flex h-full flex-col bg-panelcolor overflow-hidden w-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-vc-panel-border bg-slate-50 dark:bg-muted shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-200 dark:hover:bg-muted transition"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold text-slate-800 dark:text-foreground"
            style={{ backgroundColor: getAvatarColor(participant.name) }}
          >
            {participant.name.charAt(0).toUpperCase()}
          </div>
          <span className="font-semibold text-slate-800 dark:text-foreground">
            {participant.name}
          </span>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMenu((v) => !v)}
            className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-200 dark:hover:bg-muted transition"
          >
            <MoreVertical className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />
          </button>
          {showMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 top-full mt-1 w-40 bg-card rounded-lg shadow-lg ring-1 ring-black/5 py-1 z-50"
            >
              <button
                type="button"
                onClick={() => {
                  onSaveChat()
                  setShowMenu(false)
                }}
                className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-foreground hover:bg-slate-50 dark:hover:bg-muted transition"
              >
                {t('panels.saveChat')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onClearChat()
                  setShowMenu(false)
                }}
                className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-foreground hover:bg-slate-50 dark:hover:bg-muted transition"
              >
                {t('panels.clearChat')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mx-4 mt-3 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 shrink-0">
        <Info className="h-4 w-4 text-primary shrink-0" />
        <p className="text-xs text-primary">{t('panels.chatDisappearsOnLeave')}</p>
      </div>

      <div
        className={`flex-1 overflow-y-auto min-h-[56vh] max-h-56vh ${
          messages.length === 0 ? 'flex justify-center items-center' : ''
        } sm:min-h-0 px-4 py-3 space-y-3`}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="35" cy="45" r="12" fill="#E8D5D0" />
              <path d="M20 75C20 61.19 30.19 50 44 50H26C16.59 50 8 58.59 8 68V75H20Z" fill="#E8D5D0" />
              <rect x="48" y="25" width="32" height="24" rx="4" fill="#F5C6C6" stroke="#E8A5A5" strokeWidth="1.5" />
              <rect x="52" y="31" width="20" height="2" rx="1" fill="#E8A5A5" />
              <rect x="52" y="37" width="16" height="2" rx="1" fill="#E8A5A5" />
              <circle cx="85" cy="45" r="12" fill="#E8D5D0" />
              <path d="M70 75C70 61.19 80.19 50 94 50H76C66.59 50 58 58.59 58 68V75H70Z" fill="#E8D5D0" />
              <rect x="40" y="60" width="32" height="24" rx="4" fill="#D4E8F5" stroke="#A5C8E8" strokeWidth="1.5" />
              <rect x="44" y="66" width="20" height="2" rx="1" fill="#A5C8E8" />
              <rect x="44" y="72" width="16" height="2" rx="1" fill="#A5C8E8" />
            </svg>
            <div className="text-lg font-medium text-slate-700 dark:text-foreground">
              {t('panels.noMessagesYet')}
            </div>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderId === localUserId
            return (
              <div
                key={msg.id}
                className={['flex flex-col gap-0.5', isMe ? 'items-end' : 'items-start'].join(' ')}
              >
                {msg.attachment ? (
                  <ChatAttachmentBubble attachment={msg.attachment} isMe={isMe} />
                ) : (
                  <div
                    className={[
                      'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                      isMe
                        ? 'rounded-br-sm bg-primary text-white'
                        : 'rounded-bl-sm bg-slate-100 dark:bg-muted text-slate-800 dark:text-foreground',
                    ].join(' ')}
                  >
                    {msg.text}
                  </div>
                )}
                <div className="text-[10px] text-slate-400 dark:text-muted-foreground">
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      <ChatComposer
        text={text}
        onTextChange={setText}
        canChat={canChat}
        isPrivate
        isBroadcast={false}
        onSend={handleSend}
        onAttach={handleAttach}
        canAttachFiles={canSendFiles}
        canUseEmojis={canUseEmojis}
      />
    </div>
  )
})
